import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api/endpoints';
import type { SessionState } from '@/lib/api/types';
import { usePolling } from '@/lib/hooks/usePolling';
import type { Position } from '@/features/viewer/hooks/useWorkshopNavigation';

interface FollowModeOptions {
  cohortId: string | null;
  /** Applies the presenter's position to the viewer. */
  onPosition: (position: Position) => void;
}

export interface FollowMode {
  /** True when a cohort session is being followed. */
  available: boolean;
  following: boolean;
  presenterPosition: Position | null;
  lastUpdatedAt: string | null;
  error: string | null;
  pause: () => void;
  /** US-4.3 — one action to jump back to the presenter and resume following. */
  rejoin: () => void;
}

/**
 * Keeps the viewer aligned with the presenter's position (US-4.2, US-4.3).
 *
 * Follow mode is on by default (BR-AP5). Pausing stops applying the presenter's
 * position but keeps polling, so the banner can keep showing where the room is
 * and rejoining is instant. Polling is the transport chosen in ADR 0001 to stay
 * on the free tier; a reconnect simply returns the current position, which is
 * the "catch up after reconnect" behaviour US-4.2 asks for.
 */
export function useFollowMode({ cohortId, onPosition }: FollowModeOptions): FollowMode {
  const [following, setFollowing] = useState(true);
  const onPositionRef = useRef(onPosition);
  onPositionRef.current = onPosition;

  const { data, error } = usePolling<SessionState>(
    (signal) => api.session.get(cohortId as string, signal),
    { enabled: cohortId !== null, intervalMs: 4000 },
  );

  const presenterPosition: Position | null = data ? { module: data.module, topic: data.topic } : null;

  useEffect(() => {
    if (!following || !data) return;
    onPositionRef.current({ module: data.module, topic: data.topic });
  }, [following, data]);

  const rejoin = useCallback(() => {
    if (data) onPositionRef.current({ module: data.module, topic: data.topic });
    setFollowing(true);
  }, [data]);

  return {
    available: cohortId !== null,
    following,
    presenterPosition,
    lastUpdatedAt: data?.updatedAt ?? null,
    error,
    pause: () => setFollowing(false),
    rejoin,
  };
}
